import React, { useRef, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { MonitorIcon, CheckCircleIcon, ArrowRightIcon, ChevronRightIcon, ZapIcon, DatabaseIcon, CodeIcon, SearchIcon, MessageSquareIcon, BarChartIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { ContactForm } from '../../components/ContactForm';
import { useSEO } from '../../hooks/useSEO';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslations } from '../../i18n/translations';
function HeroSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  return <section className="relative pt-20 lg:pt-24 pb-20 overflow-hidden bg-gradient-to-br from-violet-900 via-violet-800 to-navy-900" data-id="element-1082">
      <div className="absolute inset-0" data-id="element-1083">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-violet-500/10 blur-3xl hidden sm:block" data-id="element-1084" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-primary-500/8 blur-3xl hidden sm:block" data-id="element-1085" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} data-id="element-1086" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-id="element-1087">
        <motion.nav initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="flex items-center gap-1.5 text-sm text-violet-300/70 mb-10" data-id="element-1088">
          <Link to="/" className="hover:text-violet-300 transition-colors" data-id="element-1089">
            {t.nav.home}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1090" />
          <Link to="/#services" className="hover:text-violet-300 transition-colors" data-id="element-1091">
            {t.nav.services}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1092" />
          <span className="text-violet-200" data-id="element-1093">{t.itRecruitment.title}</span>
        </motion.nav>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-1094">
          <div data-id="element-1095">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.05
          }} className="flex items-center gap-3 mb-6" data-id="element-1096">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-400 to-violet-600 text-white shadow-xl shadow-violet-500/30" data-id="element-1097">
                <MonitorIcon className="w-6 h-6" data-id="element-1098" />
              </div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-violet-500/20 text-violet-300 text-xs font-semibold rounded-full border border-violet-500/20" data-id="element-1099">
                {t.itRecruitment.badge}
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.1
          }} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.08]" data-id="element-1100">
              {t.itRecruitment.h1_1}{' '}
              <span className="text-violet-400" data-id="element-1101">
                {t.itRecruitment.h1_accent}
              </span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.18
          }} className="mt-6 text-xl text-violet-200/80 leading-relaxed" data-id="element-1102">
              {t.itRecruitment.subtitle}
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.25
          }} className="mt-8 flex flex-wrap gap-4" data-id="element-1103">
              <a href="#contact" className="inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-violet-900 bg-white rounded-xl hover:bg-violet-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-1104">
                {t.itRecruitment.cta_primary}
                <ArrowRightIcon className="w-4 h-4" data-id="element-1105" />
              </a>
              <a href="#stacks" className="inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-white border border-white/20 rounded-xl hover:bg-white/10 transition-all" data-id="element-1106">
                {t.itRecruitment.cta_secondary}
              </a>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="grid grid-cols-2 gap-4" data-id="element-1107">
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1108">
              <div className="text-4xl font-extrabold text-white" data-id="element-1109">
                {t.itRecruitment.stats.days_val}
              </div>
              <div className="text-sm text-violet-300/70 mt-1" data-id="element-1110">
                {t.itRecruitment.stats.days_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1111">
              <div className="text-4xl font-extrabold text-violet-400" data-id="element-1112">
                {t.itRecruitment.stats.stacks_val}
              </div>
              <div className="text-sm text-violet-300/70 mt-1" data-id="element-1113">
                {t.itRecruitment.stats.stacks_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1114">
              <div className="text-4xl font-extrabold text-white" data-id="element-1115">
                {t.itRecruitment.stats.closed_val}
              </div>
              <div className="text-sm text-violet-300/70 mt-1" data-id="element-1116">
                {t.itRecruitment.stats.closed_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1117">
              <div className="text-4xl font-extrabold text-violet-400" data-id="element-1118">
                {t.itRecruitment.stats.success_val}
              </div>
              <div className="text-sm text-violet-300/70 mt-1" data-id="element-1119">
                {t.itRecruitment.stats.success_label}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function StacksSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const stacks = [{
    category: 'Frontend',
    tags: ['React', 'Vue', 'Angular', 'Next.js', 'TypeScript', 'Svelte', 'Nuxt.js', 'Gatsby', 'Tailwind CSS', 'Redux', 'GraphQL', 'Webpack', 'Vite', 'Storybook', 'Web Components']
  }, {
    category: 'Backend',
    tags: ['Node.js', 'Python', 'Java', 'Go', 'PHP', 'Ruby', '.NET', 'Rust', 'C++', 'Scala', 'Kotlin', 'Spring Boot', 'Django', 'FastAPI', 'Laravel', 'Express', 'NestJS', 'gRPC']
  }, {
    category: 'Mobile',
    tags: ['iOS (Swift)', 'Android (Kotlin)', 'React Native', 'Flutter', 'Xamarin', 'Ionic', 'Objective-C', 'SwiftUI', 'Jetpack Compose', 'Expo', 'Capacitor', 'PWA', 'Mobile QA', 'App Store Optimization', 'Push Notifications']
  }, {
    category: 'DevOps & Cloud',
    tags: ['AWS', 'GCP', 'Azure', 'Docker', 'Kubernetes', 'Terraform', 'CI/CD', 'Jenkins', 'GitLab CI', 'Ansible', 'Linux', 'Nginx', 'Prometheus', 'Grafana', 'Helm', 'ArgoCD', 'Vault', 'Site Reliability']
  }, {
    category: 'Data & AI',
    tags: ['Data Science', 'ML Engineer', 'Data Engineer', 'LLM / AI', 'Python/R', 'Spark', 'Hadoop', 'Airflow', 'dbt', 'Power BI', 'Tableau', 'Data Analyst', 'MLOps', 'Computer Vision', 'NLP', 'Snowflake', 'Databricks']
  }, {
    category: 'QA & Testing',
    tags: ['QA Manual', 'QA Automation', 'Selenium', 'Cypress', 'Playwright', 'Appium', 'Postman', 'JMeter', 'TestRail', 'Load Testing', 'Performance Testing', 'Security Testing', 'API Testing', 'BDD / Cucumber', 'QA Lead']
  }, {
    category: 'Product & Management',
    tags: ['Product Manager', 'Product Owner', 'Scrum Master', 'Agile Coach', 'Business Analyst', 'Project Manager', 'Program Manager', 'Delivery Manager', 'CTO', 'VP of Engineering', 'Head of Product', 'Release Manager', 'IT Director', 'PMO', 'OKR Facilitator']
  }, {
    category: 'Design & UX',
    tags: ['UI/UX Designer', 'Product Designer', 'UX Researcher', 'Graphic Designer', 'Motion Designer', 'Figma', 'Design Lead', 'Design System', 'Interaction Designer', 'Visual Designer', 'Brand Designer', 'Illustrator', 'Web Designer', 'Design Director', 'Accessibility']
  }, {
    category: 'Marketing & Growth',
    tags: ['Digital Marketer', 'SEO/SEM', 'Performance Marketing', 'SMM', 'Content Manager', 'Email Marketing', 'Growth Hacker', 'CMO', 'Brand Manager', 'Copywriter', 'PPC Specialist', 'Affiliate Marketing', 'Marketing Analyst', 'PR Manager', 'Influencer Marketing']
  }, {
    category: 'Sales & Bizdev',
    tags: ['Sales Manager', 'Account Executive', 'BDM', 'Pre-sales', 'Customer Success', 'Partnership Manager', 'Head of Sales', 'CCO', 'SDR', 'Account Manager', 'Sales Ops', 'Revenue Manager', 'Inside Sales', 'Enterprise Sales', 'Channel Sales']
  }, {
    category: 'Security & Infra',
    tags: ['Cybersecurity', 'Penetration Tester', 'SOC Analyst', 'Security Engineer', 'Network Engineer', 'System Administrator', 'DBA', 'PostgreSQL', 'MongoDB', 'Redis', 'MySQL', 'Oracle DBA', 'Elasticsearch', 'CISO', 'Cloud Security']
  }, {
    category: 'Finance & Operations',
    tags: ['CFO', 'Financial Analyst', 'Controller', 'HR Business Partner', 'Recruiter', 'Legal Counsel', 'COO', 'Operations Manager', 'IT Procurement', 'Vendor Manager', 'Compliance Officer', 'Risk Manager', 'IT Auditor', 'Finance Director', 'People Partner']
  }];
  return <section className="py-20 lg:py-28 bg-white" id="stacks" ref={ref} data-id="element-1120">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1121">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-1122">
          <span className="text-sm font-semibold text-violet-600 tracking-wide uppercase" data-id="element-1123">
            {t.itRecruitment.stacks.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-1124">
            {t.itRecruitment.stacks.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-1125">
            {t.itRecruitment.stacks.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1126">
          {stacks.map((stack, i) => <motion.div key={stack.category} initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="p-6 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1127">
              <div className="flex items-center gap-2 mb-4" data-id="element-1128">
                <CodeIcon className="w-4 h-4 text-violet-600" data-id="element-1129" />
                <h3 className="font-bold text-navy-900" data-id="element-1130">{stack.category}</h3>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-1131">
                {stack.tags.map(tag => <span key={tag} className="text-xs font-semibold px-2.5 py-1 bg-violet-50 text-violet-700 rounded-lg border border-violet-100" data-id="element-1132">
                    {tag}
                  </span>)}
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
function WhyUsSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-navy-50/40" ref={ref} data-id="element-1133">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1134">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-1135">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-1136">
            <span className="text-sm font-semibold text-violet-600 tracking-wide uppercase" data-id="element-1137">
              {t.itRecruitment.whyUs.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-navy-900 tracking-tight" data-id="element-1138">
              {t.itRecruitment.whyUs.title}
            </h2>
            <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-1139">
              {t.itRecruitment.whyUs.subtitle}
            </p>
            <div className="mt-8 space-y-4" data-id="element-1140">
              <div className="flex items-start gap-3" data-id="element-1141">
                <CheckCircleIcon className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" data-id="element-1142" />
                <div data-id="element-1143">
                  <div className="font-semibold text-navy-900" data-id="element-1144">
                    {t.itRecruitment.whyUs.points[0].title}
                  </div>
                  <div className="text-sm text-navy-500 mt-0.5" data-id="element-1145">
                    {t.itRecruitment.whyUs.points[0].desc}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1146">
                <CheckCircleIcon className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" data-id="element-1147" />
                <div data-id="element-1148">
                  <div className="font-semibold text-navy-900" data-id="element-1149">
                    {t.itRecruitment.whyUs.points[1].title}
                  </div>
                  <div className="text-sm text-navy-500 mt-0.5" data-id="element-1150">
                    {t.itRecruitment.whyUs.points[1].desc}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1151">
                <CheckCircleIcon className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" data-id="element-1152" />
                <div data-id="element-1153">
                  <div className="font-semibold text-navy-900" data-id="element-1154">
                    {t.itRecruitment.whyUs.points[2].title}
                  </div>
                  <div className="text-sm text-navy-500 mt-0.5" data-id="element-1155">
                    {t.itRecruitment.whyUs.points[2].desc}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1156">
                <CheckCircleIcon className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" data-id="element-1157" />
                <div data-id="element-1158">
                  <div className="font-semibold text-navy-900" data-id="element-1159">
                    {t.itRecruitment.whyUs.points[3].title}
                  </div>
                  <div className="text-sm text-navy-500 mt-0.5" data-id="element-1160">
                    {t.itRecruitment.whyUs.points[3].desc}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="space-y-4" data-id="element-1161">
            <div className="p-6 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1162">
              <div className="flex items-center gap-3 mb-3" data-id="element-1163">
                <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-1164">
                  <ZapIcon className="w-5 h-5" data-id="element-1165" />
                </div>
                <div className="font-bold text-navy-900" data-id="element-1166">
                  {t.itRecruitment.whyUs.cards[0].title}
                </div>
              </div>
              <p className="text-sm text-navy-500" data-id="element-1167">
                {t.itRecruitment.whyUs.cards[0].desc}
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1168">
              <div className="flex items-center gap-3 mb-3" data-id="element-1169">
                <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-1170">
                  <DatabaseIcon className="w-5 h-5" data-id="element-1171" />
                </div>
                <div className="font-bold text-navy-900" data-id="element-1172">
                  {t.itRecruitment.whyUs.cards[1].title}
                </div>
              </div>
              <p className="text-sm text-navy-500" data-id="element-1173">
                {t.itRecruitment.whyUs.cards[1].desc}
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1174">
              <div className="flex items-center gap-3 mb-3" data-id="element-1175">
                <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-1176">
                  <MessageSquareIcon className="w-5 h-5" data-id="element-1177" />
                </div>
                <div className="font-bold text-navy-900" data-id="element-1178">
                  {t.itRecruitment.whyUs.cards[2].title}
                </div>
              </div>
              <p className="text-sm text-navy-500" data-id="element-1179">
                {t.itRecruitment.whyUs.cards[2].desc}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function CtaBanner() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5
  }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1180">
      <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-violet-900 via-violet-800 to-navy-900 relative overflow-hidden" data-id="element-1181">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-violet-500/15 blur-3xl hidden sm:block" data-id="element-1182" />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-primary-400/10 blur-3xl hidden sm:block" data-id="element-1183" />
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10" data-id="element-1184">
          <div className="flex-1" data-id="element-1185">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-1186">
              {t.itRecruitment.ctaBanner.title}
            </h3>
            <p className="mt-2 text-violet-200/80 leading-relaxed" data-id="element-1187">
              {t.itRecruitment.ctaBanner.subtitle}
            </p>
          </div>
          <a href="#contact" className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-violet-900 bg-white rounded-xl hover:bg-violet-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-1188">
            {t.itRecruitment.ctaBanner.button}
            <ArrowRightIcon className="w-4 h-4" data-id="element-1189" />
          </a>
        </div>
      </div>
    </motion.div>;
}
const seoData = {
  UA: {
    title: 'IT Recruitment — Підбір IT-спеціалістів | ESCA',
    description: 'Підбір IT-спеціалістів будь-якого рівня: розробники, DevOps, QA, Product Manager, дизайнери. 50+ технологічних стеків, 1500+ закритих IT-вакансій.',
    keywords: 'IT рекрутинг, підбір IT спеціалістів, розробники, DevOps, QA, React, Python, Java, ESCA'
  },
  RU: {
    title: 'IT Recruitment — Подбор IT-специалистов | ESCA',
    description: 'Подбор IT-специалистов любого уровня: разработчики, DevOps, QA, Product Manager, дизайнеры. 50+ технологических стеков, 1500+ закрытых IT-вакансий.',
    keywords: 'IT рекрутинг, подбор IT специалистов, разработчики, DevOps, QA, React, Python, Java, ESCA'
  },
  EN: {
    title: 'IT Recruitment — Hire Top Tech Talent | ESCA',
    description: 'IT recruitment for all levels: developers, DevOps, QA, Product Managers, designers. 50+ tech stacks covered, 1500+ IT positions filled.',
    keywords: 'IT recruitment, tech hiring, developers, DevOps, QA, React, Python, Java, ESCA'
  }
};
export function ITRecruitmentPage() {
  const {
    lang
  } = useLanguage();
  const seo = seoData[lang];
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords
  });
  return <div className="min-h-screen w-full bg-white overflow-x-hidden" data-id="element-1190">
      <Header data-id="element-1191" />
      <main className="pt-16 lg:pt-20" data-id="element-1192">
        <HeroSection data-id="element-1193" />
        <StacksSection data-id="element-1194" />
        <WhyUsSection data-id="element-1195" />
        <CtaBanner data-id="element-1196" />
        <div id="contact" data-id="element-1197">
          <ContactForm data-id="element-1198" />
        </div>
      </main>
      <Footer data-id="element-1199" />
    </div>;
}